// Content-defined chunking for kinedb files (CDC v1). No dependency.
//
// The client splits a file at content boundaries, so an edit near the start of
// a file does not move the chunks after it. The store then dedups by content.
//
// Three implementations exist and they must agree byte for byte:
//
//   * `crates/kinedb-chunker` (Rust),
//   * `sdk/python/client/src/kinedb/client/chunker.py` (Python),
//   * this module.
//
// The checked-in vector `sdk/test-vectors/cdc-v1.json` proves the three agree.
//
// The reference is "h mod 2^32"
// -----------------------------
//
// The gear table holds 64-bit values, but the rolling hash reads only the LOW
// 32 BITS of each entry and wraps at 2^32. JavaScript has no cheap 64-bit
// integer, so the reference arithmetic is 32 bits wide in every language. Only
// the low 16 bits of the hash feed the mask, and `(h << 1) + g` carries from
// the low bits to the high bits only, so a 32-bit window keeps every bit the
// mask reads.

// The smallest chunk a boundary can close, in bytes.
export const MIN_CHUNK_BYTES = 16 * 1024;

// The average chunk size the mask targets, in bytes.
export const AVG_CHUNK_BYTES = 64 * 1024;

// The largest chunk. The chunker forces a boundary here.
export const MAX_CHUNK_BYTES = 128 * 1024;

// The number of mask bits. 16 bits give one boundary every 64 KiB on average.
export const MASK_BITS = 16;

// The seed of the gear table. The ASCII bytes of "kinedb_c".
export const GEAR_SEED = 0x6b696e6564625f63n;

const MASK = (1 << MASK_BITS) - 1;
const M64 = 0xffffffffffffffffn;

// Return 256 splitmix64 outputs, seeded with GEAR_SEED.
function buildGearTable() {
  const table = new BigUint64Array(256);
  let state = GEAR_SEED;
  for (let i = 0; i < 256; i += 1) {
    state = (state + 0x9e3779b97f4a7c15n) & M64;
    let z = state;
    z = ((z ^ (z >> 30n)) * 0xbf58476d1ce4e5b9n) & M64;
    z = ((z ^ (z >> 27n)) * 0x94d049bb133111ebn) & M64;
    table[i] = z ^ (z >> 31n);
  }
  return table;
}

const GEAR = buildGearTable();

// The low 32 bits of every gear entry. The rolling hash reads only these.
const GEAR32 = new Uint32Array(256);
for (let i = 0; i < 256; i += 1) GEAR32[i] = Number(GEAR[i] & 0xffffffffn);

// Return a copy of the 256-entry 64-bit gear table.
export function gearTable() {
  return GEAR.slice();
}

// Return the END offset of every chunk of `data`.
// The last offset is always `data.length`. An empty input gives an empty array.
export function chunkBoundaries(data) {
  const size = data.length;
  const out = [];
  let start = 0;
  while (start < size) {
    // The first MIN-1 bytes can never close a chunk. They only roll the hash,
    // so the mask test stays out of that loop.
    const warmEnd = Math.min(start + MIN_CHUNK_BYTES - 1, size);
    let h = 0;
    for (let i = start; i < warmEnd; i += 1) {
      h = (((h << 1) >>> 0) + GEAR32[data[i]]) >>> 0;
    }

    const hardEnd = Math.min(start + MAX_CHUNK_BYTES, size);
    let end = hardEnd;
    for (let i = warmEnd; i < hardEnd; i += 1) {
      h = (((h << 1) >>> 0) + GEAR32[data[i]]) >>> 0;
      if ((h & MASK) === 0) {
        end = i + 1;
        break;
      }
    }

    out.push(end);
    start = end;
  }
  return out;
}

// Return every chunk of `data` as a Uint8Array that shares the input buffer.
export function chunks(data) {
  const out = [];
  let start = 0;
  for (const end of chunkBoundaries(data)) {
    out.push(data.subarray(start, end));
    start = end;
  }
  return out;
}
